import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  closeSupportTicket,
  editSupportMessage,
  getSupportTicket,
  markSupportTicketRead,
  postSupportMessage,
} from "../../services/supportService";
import BackLink from "../../components/navigation/BackLink/BackLink";
import { SkeletonCard } from "../../components/Skeleton/Skeleton";
import Composer from "../../components/support/Composer";
import MessageThread from "../../components/support/MessageThread";
import TicketStatusBadge from "../../components/support/TicketStatusBadge";
import { SUPPORT_STATUS } from "../../utils/constants/support";
import { formatApiError } from "../../utils/helpers/apiErrors";
import { formatDateTime } from "../../utils/helpers/dateDisplay";
import { supportErrorMessage } from "../../utils/helpers/supportErrors";
import useSupportStore from "../../utils/stores/useSupportStore";

// Support replies arrive from Telegram whenever staff answer, so an open
// thread re-reads itself — but only while the tab is visible.
const POLL_MS = 15000;

const isCanceled = (err) => err?.name === "CanceledError" || err?.code === "ERR_CANCELED";

export default function SupportThread() {
  const { uuid } = useParams();
  const { t } = useTranslation();
  const refreshUnread = useSupportStore((s) => s.refreshUnread);
  const [ticket, setTicket] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | notFound | error
  const [reloadKey, setReloadKey] = useState(0);
  const [closing, setClosing] = useState(false);
  const bottomRef = useRef(null);

  // Opening the thread is reading it: clear the unread marker server-side and
  // let the sidebar badge drop right away rather than at its next poll.
  const markRead = useCallback(
    (loaded) => {
      if (!loaded?.has_unread) return;
      markSupportTicketRead({ uuid: loaded.uuid })
        .then((fresh) => {
          setTicket(fresh);
          refreshUnread();
        })
        .catch(() => {
          /* stays unread; the next poll tries again */
        });
    },
    [refreshUnread],
  );

  useEffect(() => {
    const controller = new AbortController();
    let timer = null;

    const schedule = () => {
      if (!controller.signal.aborted) timer = setTimeout(tick, POLL_MS);
    };
    const load = (initial) => {
      getSupportTicket({ uuid, signal: controller.signal })
        .then((data) => {
          setTicket(data);
          setState("ready");
          markRead(data);
          schedule();
        })
        .catch((err) => {
          if (isCanceled(err)) return;
          if (initial) {
            setState(err?.response?.status === 404 ? "notFound" : "error");
            return;
          }
          schedule(); // a failed poll is not worth a message; try again later
        });
    };
    function tick() {
      if (document.visibilityState === "visible") load(false);
      else schedule();
    }

    setState("loading");
    load(true);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [uuid, reloadKey, markRead]);

  // Keep the newest message in view as the thread grows.
  const messageCount = ticket?.messages?.length || 0;
  useEffect(() => {
    if (messageCount) bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messageCount]);

  const send = async ({ body, files }) => {
    try {
      setTicket(await postSupportMessage({ uuid, body: { body, files } }));
    } catch (err) {
      toast.error(supportErrorMessage(err, t, t("support.thread.sendFailed")));
      throw err; // keeps the draft in the composer
    }
  };

  const edit = async (message, body) => {
    try {
      setTicket(await editSupportMessage({ uuid, messageUuid: message.uuid, body: { body } }));
    } catch (err) {
      toast.error(supportErrorMessage(err, t, t("support.thread.editFailed")));
      throw err; // keeps the draft in the editor
    }
  };

  const resolve = async () => {
    if (!window.confirm(t("support.thread.resolveConfirm"))) return;
    setClosing(true);
    try {
      setTicket(await closeSupportTicket({ uuid }));
    } catch (err) {
      toast.error(formatApiError(err, t("support.thread.closeFailed")));
    } finally {
      setClosing(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="flex-1 bg-slate-50 p-6">
        <div className="max-w-3xl mx-auto space-y-3">
          <SkeletonCard lines={2} />
          <SkeletonCard lines={4} />
        </div>
      </div>
    );
  }

  if (state === "notFound" || state === "error") {
    return (
      <div className="flex-1 bg-slate-50 p-6">
        <div className="max-w-3xl mx-auto space-y-5">
          <BackLink to="/support" labelKey="support.backToList" />
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center space-y-4">
            <p className="text-sm font-bold text-slate-700">
              {state === "notFound" ? t("support.thread.notFound") : t("support.loadFailed")}
            </p>
            {state === "error" && (
              <button
                type="button"
                onClick={() => setReloadKey((k) => k + 1)}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700"
              >
                {t("common.retry")}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const closed = ticket.status === SUPPORT_STATUS.CLOSED;

  return (
    <div className="flex-1 bg-slate-50 p-6">
      <div className="max-w-3xl mx-auto space-y-5">
        <BackLink to="/support" labelKey="support.backToList" />

        <header className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              #{ticket.number} · {t(`support.category.${ticket.category}`)}
            </p>
            <h2 className="text-lg font-black text-slate-900 mt-1 break-words">{ticket.subject}</h2>
            <p className="text-xs text-slate-500 font-medium mt-1">
              {t("support.thread.openedOn", { date: formatDateTime(ticket.created_at) })}
            </p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <TicketStatusBadge status={ticket.status} />
            {!closed && (
              <button
                type="button"
                onClick={resolve}
                disabled={closing}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-bold hover:bg-emerald-100 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
                {t("support.thread.resolve")}
              </button>
            )}
          </div>
        </header>

        <section
          aria-label={t("support.thread.conversation")}
          className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5"
        >
          <MessageThread messages={ticket.messages} viewer="user" onEdit={edit} />
        </section>

        {closed && (
          <p className="rounded-2xl border border-slate-200 bg-slate-100 px-5 py-3 text-xs font-semibold text-slate-600">
            {t("support.thread.closedBanner")}
          </p>
        )}

        <Composer onSend={send} placeholder={t("support.thread.replyPlaceholder")} />
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
